import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { Copy, Check } from '@phosphor-icons/react';
import CollapsibleSection from './CollapsibleSection';
import JsonInputField from '../../../../../Utility/JsonInputField/JsonInputField';
import StatusBadge from './StatusBadge';
import useGlobals from '../../../../../../Hooks/useGlobals';

interface TestResult {
    request: {
        method: string;
        url: string;
        parameters: any;
    };
    response: {
        statusCode: number;
        executionTime: number;
        headers: any;
        body: any;
    };
}

interface TestResultsProps {
    testResult: TestResult | null;
    copied: { [key: string]: boolean };
    onCopy: (text: string, key: string) => void;
    responseOpen: boolean;
    setResponseOpen: React.Dispatch<React.SetStateAction<boolean>>;
    headersOpen: boolean;
    setHeadersOpen: React.Dispatch<React.SetStateAction<boolean>>;
    requestOpen: boolean;
    setRequestOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TestResults: React.FC<TestResultsProps> = ({
    testResult,
    copied,
    onCopy,
    responseOpen,
    setResponseOpen,
    headersOpen,
    setHeadersOpen,
    requestOpen,
    setRequestOpen,
}) => {
    const { i18n } = useGlobals();
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (testResult && containerRef.current) {
            const timeoutId = setTimeout(() => {
                const el = containerRef.current!;
                const desiredOffset = 120; //Header + extra margin
                const top = el.getBoundingClientRect().top + window.pageYOffset - desiredOffset;
                window.scrollTo({
                    top: Math.max(0, top),
                    behavior: 'smooth',
                });
            }, 50);

            return () => clearTimeout(timeoutId);
        }
    }, [testResult, setResponseOpen, setHeadersOpen, setRequestOpen]);

    useLayoutEffect(() => {
        setResponseOpen(true);
        setHeadersOpen(false);
        setRequestOpen(true);
    }, [setResponseOpen, setHeadersOpen, setRequestOpen, testResult]);

    if (!testResult) {
        return null;
    }

    const formatJSON = (obj: any): string => {
        try {
            return JSON.stringify(obj, null, 2);
        } catch {
            return String(obj);
        }
    };

    return (
        <div ref={containerRef} className="pt-0">
            <div className="flex flex-col gap-4">
                {/* Results summary */}
                <div className="mb-0 flex items-center gap-2">
                    <h4 className="m-0 p-0">
                        {i18n.translate({ id: 'testing.results.title', sourceName: 'OpenAPI' })}
                    </h4>
                    <div onClick={() => onCopy(formatJSON(testResult), 'full-result')}>
                        <span className="h-full w-full text-layout-textcolor hover:text-primary flex items-center gap-1 transition-colors">
                            {copied['full-result'] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </span>
                    </div>
                </div>

                {/* </CollapsibleSections> */}
                {/* Request Details */}
                <CollapsibleSection
                    title={i18n.translate({ id: 'testing.request.title', sourceName: 'OpenAPI' })}
                    isOpen={requestOpen}
                    onToggle={() => setRequestOpen((s) => !s)}
                    titleTooltip={requestOpen ? 'Hide request' : 'Show request'}
                >
                    <div className="mt-3 py-3 px-5">
                        <div className="flex flex-col justify-center gap-2 mb-4">
                            <div className="flex gap-3 items-center ">
                                <StatusBadge type="method" value={testResult.request.method} />
                                <code className="text-sm text-layout-textcolor">{testResult.request.url}</code>
                            </div>
                            <div className="flex gap-3 items-center ">
                                <StatusBadge type="status" value={testResult.response.statusCode} />
                                <span className="text-sm text-gray-500">{testResult.response.executionTime}ms</span>
                            </div>
                        </div>
                        {Object.keys(testResult.request.parameters).length > 0 && (
                            <div className="rounded border border-layout-borderColor">
                                <JsonInputField
                                    value={testResult.request.parameters}
                                    readOnly={true}
                                    showCopyButton={true}
                                    onCopy={(text) => onCopy(text, 'request-parameters')}
                                    minRows={3}
                                    maxRows={15}
                                />
                            </div>
                        )}
                    </div>
                </CollapsibleSection>

                {/* Response Headers */}
                {Object.keys(testResult.response.headers).length > 0 && (
                    <CollapsibleSection
                        title={i18n.translate({ id: 'testing.headers.title', sourceName: 'OpenAPI' })}
                        isOpen={headersOpen}
                        onToggle={() => setHeadersOpen((s) => !s)}
                        titleTooltip={headersOpen ? 'Hide headers' : 'Show headers'}
                    >
                        <div className="mt-3 py-3 px-5">
                            <div className="rounded border border-layout-borderColor">
                                <JsonInputField
                                    value={testResult.response.headers}
                                    readOnly={true}
                                    showCopyButton={true}
                                    onCopy={(text) => onCopy(text, 'response-headers')}
                                    minRows={3}
                                    maxRows={15}
                                />
                            </div>
                        </div>
                    </CollapsibleSection>
                )}

                {/* Response Body */}
                <CollapsibleSection
                    title={i18n.translate({ id: 'testing.response.title', sourceName: 'OpenAPI' })}
                    isOpen={responseOpen}
                    onToggle={() => setResponseOpen((s) => !s)}
                    titleTooltip={responseOpen ? 'Hide response' : 'Show response'}
                >
                    <div className="mt-3 py-3 px-5 ">
                        <div className="rounded border border-layout-borderColor">
                            <JsonInputField
                                value={testResult.response.body}
                                readOnly={true}
                                showCopyButton={true}
                                onCopy={(text) => onCopy(text, 'response-body')}
                                minRows={5}
                                maxRows={20}
                            />
                        </div>
                    </div>
                </CollapsibleSection>
            </div>
        </div>
    );
};

export default TestResults;
